'use client'

import type { ReactNode } from 'react'

import { useEffect, useState } from 'react'
import parse from 'html-react-parser'

import useStore from '@/stores/Store'

import '../sections.css'

// oxlint-disable-next-line no-restricted-imports -- hydration-safe: see ARCHITECTURE.md trap #1
import { useMediaQuery } from 'react-responsive'

interface JumbotronBanProps {
  badgeText?: string
  title: string
  buttonText?: ReactNode
  footNotes?: ReactNode
  /** Primary (desktop) background image URL. */
  backgroundImage?: string
  /**
   * Optional mobile background image. When provided it replaces
   * `backgroundImage` at ≤767px via a <picture> media source — CSS-first, so
   * the frozen HTML is correct on every device at first paint and the browser
   * downloads only the matching source. Callers must NOT pick the image in JS
   * (a useIsMobile/useMediaQuery branch at the call site ships the desktop
   * asset to phones and swaps it after mount).
   */
  mobileBackgroundImage?: string
}

function JumbotronBan({
  badgeText,
  title,
  buttonText,
  footNotes,
  backgroundImage,
  mobileBackgroundImage
}: JumbotronBanProps) {
  const { formRef } = useStore()
  const isDesktop = useMediaQuery({ minWidth: 768 })
  const isMobile = useMediaQuery({ maxWidth: 480 })

  // useMediaQuery returns false on the server, so the media-query-derived
  // objectPosition differs between SSR and the first client render and triggers a
  // hydration attribute mismatch. Render a stable default until mounted.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  const objectPosition = mounted
    ? isMobile
      ? 'center'
      : isDesktop
        ? 'center right'
        : 'bottom'
    : 'center right'

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: '20px',
        overflow: 'hidden',
        minHeight: '45vh',

        background:
          'linear-gradient(90deg, #ffffff 0%, #ffffff 35%, #eef4ff 55%, #e4eeff 75%, #dce9ff 100%);Programmer'
      }}
    >
      {/*
        React auto-preloads an eager/high-priority <img>, but it cannot do so
        once the <img> sits inside a <picture> — it has no way to know which
        <source> the browser will pick. Declare the preloads explicitly instead
        (React 19 hoists <link rel="preload"> into <head>), scoping each to the
        breakpoint that will actually use it so the hero keeps its LCP
        head-start on desktop AND phones fetch only the mobile asset.
      */}
      {backgroundImage &&
        (mobileBackgroundImage ? (
          <>
            <link
              rel="preload"
              as="image"
              href={backgroundImage}
              media="(min-width: 768px)"
              fetchPriority="high"
            />
            <link
              rel="preload"
              as="image"
              href={mobileBackgroundImage}
              media="(max-width: 767px)"
              fetchPriority="high"
            />
          </>
        ) : (
          <link rel="preload" as="image" href={backgroundImage} fetchPriority="high" />
        ))}

      {backgroundImage && (
        <picture>
          {mobileBackgroundImage && (
            <source media="(max-width: 767px)" srcSet={mobileBackgroundImage} />
          )}
          <img
            src={backgroundImage}
            alt=""
            fetchPriority="high"
            loading="eager"
            decoding="async"
            aria-hidden="true"
            width="100%"
            height="100%"
            style={{
              position: 'absolute',
              inset: 0,
              objectFit: 'cover',
              objectPosition,
              zIndex: 0,
              borderRadius: '20px'
            }}
          />
        </picture>
      )}

      <div className="container jumbotronBanner" style={{ position: 'relative', zIndex: 2 }}>
        <div className={`jumbotron-container ${!backgroundImage ? 'row-height-unsetter' : ''}`}>
          {badgeText && <div className="jumbotron-title col-xl-2">{parse(badgeText)}</div>}

          <h1 className="jumbotron-subtitle text-center text-xl-start">{parse(title)}</h1>

          {buttonText && (
            <button
              className="btn btn-primary btn-lg applybtn"
              type="button"
              onClick={() => {
                if (formRef.current) {
                  const offset = 410
                  const top =
                    formRef.current.getBoundingClientRect().top + window.pageYOffset - offset
                  window.scrollTo({ top, behavior: 'smooth' })
                }
              }}
            >
              {buttonText}
            </button>
          )}

          {footNotes && (
            <div className="jumbotron-note">
              <i className="bi bi-shield me-2" />
              {footNotes}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default JumbotronBan
